/* global console, setTimeout */

import {
  CoObjectItem,
  CoObjectParameters,
  DimensionDetails,
  TgkAmountObjectSource,
  TgkTableObjectSource,
} from "../../components/interfaces/CoObject";
import {
  notifyFailedElaboration,
  confirmElaborationItemsResultAddAndUserChecked,
  confirmElaborationItemsResultAddSilent,
  ElaborationRequest,
  UpdateOptions,
} from "../../api/object/RefreshObject";
import {
  getUpdatedDimensions,
  getUpdatedParameters,
  mapCoObjectToRefreshElaborationItem,
} from "../../redux/feature/cpm/map-objects";
import * as uuid from "uuid";
import {
  createElaborationRequestAsync,
  ElaborationMessage,
  ElaborationStatus,
  ElaborationStatusItem,
  elaborationStatusItemIsToAdd,
  elaborationStatusItemIsToHandleAbort,
  flagElaborationItemResultAdded,
  flagElaborationUserChecked,
  selectAbortedElaborations,
  selectElaborationsWithNotRunningAndNotCheckedItems,
} from "../../redux/feature/object/remote-elaborations-slice";
import NotificationManager, { NotificationToastToAdd } from "../../common/notification/NotificationManager";
import { formatMessage, formatMessageWithParams } from "./LanguageHelper";
import { RootState, store } from "../../redux/store/store";
import {
  cleanDocumentFromCC,
  handleCloneElabResult,
  handleDetachElabResult,
  updateContentControlWithElabResult,
  updateWebContentControlWithElabResult,
} from "../../office/ContentControl";
import { fetchDocumentMetadata, fetchDocumentPartDetails } from "../../api/document/DocumentsPart";
import { abortRefreshAsync } from "../../redux/feature/cpm/cpm-slice";
import { TableUpdateStrategy } from "../../components/coobjects/co-panel/ObjectPanelComponent";
import { handleDownloadCopyElabResult } from "../../office/DownloadCopy";
import { inheritComponentData } from "./ComponentInstanceHelper";
import { isScenarioMetadata } from "../../components/coobjects/co-panel/ReferringScenarios";
import { getOriginalDimensionFromFather } from "../../components/coobjects/co-panel/DimensionsHelpers";
import { DocumentParameters } from "../../components/document/ParametersDocument";

export const checkAbortable = (item: ElaborationStatusItem) => {
  return (
    item.elaborationType !== "CHANGE_DOC_STRUCTURE" &&
    item.elaborationType !== "DETACH" &&
    item.elaborationType !== "SHEET_AND_AREA"
  );
};

export async function launchRefreshElaboration(
  username: string,
  coObjects: CoObjectItem[],
  selectedUpdateOption?: UpdateOptions,
  tableUpdateType?: TableUpdateStrategy["typeUpdate"]
) {
  const clientID = store.getState().configurationState.clientId;
  const correlationId = uuid.v4();

  if (!coObjects || coObjects.length == 0) {
    throw new Error("empty CoObjectItem array");
  }
  const documentId = coObjects[0].documentId;

  const rootDocument = await fetchDocumentPartDetails(documentId);
  const fatherMetadata = await fetchDocumentMetadata(rootDocument.id);
  const inheritableParameters: DocumentParameters[] = [];

  for (const pm of fatherMetadata.parametersMetadata) {
    const inheritableDbParameters: DimensionDetails[] = [];
    for (const dbParam of pm.dimensions) {
      if (!isScenarioMetadata(dbParam) || dbParam.values[0]?.code) {
        inheritableDbParameters.push(dbParam);
        continue;
      }
      const thisItem = await getOriginalDimensionFromFather(dbParam, pm.dbId);
      if (!thisItem) {
        inheritableDbParameters.push(dbParam);
        continue;
      }
      inheritableDbParameters.push({
        ...dbParam,
        values: dbParam.values.map((v) => ({ ...v, code: thisItem.code })),
      });
    }
    inheritableParameters.push({ dimensions: inheritableDbParameters, dbId: pm.dbId });
  }

  const coObjectsWithInstanceComponents = await Promise.all(
    coObjects.map(async (coObject) => {
      if (coObject.info?.componentReference) {
        if (coObject.info?.componentReference?.componentLogicId?.length) {
          return await inheritComponentData(coObject);
        }
      }
      return coObject;
    })
  );

  const elaborationRequest: ElaborationRequest = {
    correlationId: correlationId,
    clientId: clientID,
    documentId: documentId,
    userId: username,
    tgkElaborationItemList: await Promise.all(
      coObjectsWithInstanceComponents.map((coObject) => {
        const dbId = (coObject.source as TgkTableObjectSource)?.dbApplication?.id;
        if (
          fatherMetadata &&
          dbId &&
          (coObject.parameters?.length || (coObject.source as TgkAmountObjectSource)?.dimensions?.length) &&
          !coObject?.info?.componentReference?.componentLogicId?.length
        ) {
          const inheritableDbParameters = inheritableParameters?.find((p) => p.dbId === dbId)?.dimensions;
          if (coObject.parameters)
            coObject.parameters = getUpdatedParameters(
              coObject.parameters as CoObjectParameters,
              inheritableDbParameters
            ).newParameters;
          if ((coObject.source as TgkAmountObjectSource)?.dimensions) {
            (coObject.source as TgkAmountObjectSource).dimensions = getUpdatedDimensions(
              (coObject.source as TgkAmountObjectSource).dimensions,
              inheritableDbParameters
            ).newDimensions;
          }
        }
        return mapCoObjectToRefreshElaborationItem(
          correlationId,
          rootDocument,
          coObject,
          selectedUpdateOption,
          tableUpdateType
        );
      })
    ),
  };

  const elaborationResponse = await createElaborationRequestAsync(elaborationRequest);

  console.log("elaborationResponse: ", elaborationResponse);
}

const onConfirmElaboration = (elaboration: ElaborationStatus) => {
  confirmElaborationItemsResultAddAndUserChecked(
    elaboration.correlationId,
    elaboration.items.map((item) => item.id),
    elaboration.items.map((item) => item.id)
  );
  store.dispatch(flagElaborationUserChecked({ correlationId: elaboration.correlationId }));
};

let lastMessageTimestamp: number = 0;
let isProcessing: boolean = false;
let instances = 0;

export const handleState = async (state: RootState) => {
  const startTimestamp = Date.now();
  lastMessageTimestamp = startTimestamp;

  while (isProcessing || Date.now() - startTimestamp > 10000) {
    if (startTimestamp !== lastMessageTimestamp) {
      instances--;
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 200));
  }

  if (Date.now() - startTimestamp > 10000)
    console.warn("handleState timeout bypassing isProcessing = TRUE (has been waiting over 10s)", {
      startTimestamp,
      instances,
    });

  isProcessing = true;

  try {
    let elaborationsToHandle = selectAbortedElaborations(state);

    if (!elaborationsToHandle?.length) {
      elaborationsToHandle = selectElaborationsWithNotRunningAndNotCheckedItems(state);
    }

    for (const elaboration of elaborationsToHandle) {
      if (startTimestamp !== lastMessageTimestamp) break;

      const addedElaborationItemsList = store.getState().remoteElaborations.addedElaborationItemsList;
      const pendingElaborationItems = elaboration.items.filter(
        (item) =>
          (elaborationStatusItemIsToAdd(item) || elaborationStatusItemIsToHandleAbort(item)) &&
          !addedElaborationItemsList.find(
            (detail) => detail.correlationId === elaboration.correlationId && detail.itemId === item.id
          )
      );
      const itemWithContentAddingErrors: ElaborationMessage[] = [];

      for (const item of pendingElaborationItems) {
        if (startTimestamp !== lastMessageTimestamp) break;
        try {
          if (item.contentControlId?.length && item.status === "ABORTED" && item.elaborationType === "CLONE") {
            await cleanDocumentFromCC(item.contentControlId);
          } else if (item.status !== "ABORTED") {
            if (item.elaborationType === "DETACH") await handleDetachElabResult(item);
            if (item.elaborationType === "CLONE") await handleCloneElabResult(item);

            if (item.elaborationType === "DOWNLOAD_COPY") await handleDownloadCopyElabResult(item);
            else {
              console.time("WordJSElaborationHandling");
              const clientType = store.getState().configurationState.clientId;
              if (clientType === "WEB") {
                await updateWebContentControlWithElabResult(item);
              } else {
                await updateContentControlWithElabResult(item);
              }
              console.timeEnd("WordJSElaborationHandling");
            }
          }

          confirmElaborationItemsResultAddSilent(item.id);
          store.dispatch(flagElaborationItemResultAdded({ correlationId: elaboration.correlationId, itemId: item.id }));
        } catch (er) {
          console.log(`error trying to add content to document object with name ${item.objectName}: ${er}`, {
            itemId: item.id,
            correlationId: item.correlationId,
            objectName: item.objectName,
            originalItem: item,
            originalError: er,
          });
          await notifyFailedElaboration(item.id, er.toString());
        }
      }

      const freshAddedElaborationItemsList = store.getState().remoteElaborations.addedElaborationItemsList;
      if (
        elaboration.items.every(
          (item) =>
            item.status !== "STARTED" &&
            item.status !== "REQUESTED" &&
            ((item.status !== "COMPLETED" && item.status !== "ABORTED") ||
              item.resultAdded ||
              freshAddedElaborationItemsList.find(
                (detail) => detail.correlationId === elaboration.correlationId && detail.itemId === item.id
              ))
        )
      ) {
        let errorsAndWarnings: { errors: ElaborationMessage[]; warnings: ElaborationMessage[] } = {
          errors: itemWithContentAddingErrors,
          warnings: [],
        };
        errorsAndWarnings = elaboration.items
          .flatMap(
            (item) =>
              item.messages?.map((msg) => {
                return { ...msg, text: item.objectName };
              }) ?? []
          )
          .reduce((status, msg) => {
            if (msg.severity === "ERROR") return { ...status, errors: [...status.errors, msg] };
            if (msg.severity === "WARNING") return { ...status, warnings: [...status.warnings, msg] };
            return status;
          }, errorsAndWarnings);

        let operationName:
          | "insertobject"
          | "editobject"
          | "import"
          | "export"
          | "duplicate"
          | "detach"
          | "clone"
          | "updateobject"
          | "changedocumentstructure";
        if (elaboration.items[0]?.elaborationType.toLowerCase() === "add") operationName = "insertobject";
        else if (elaboration.items[0]?.elaborationType.toLowerCase() === "edit") operationName = "editobject";
        else if (elaboration.items[0]?.elaborationType.toLowerCase() === "refresh") operationName = "updateobject";
        else if (elaboration.items[0]?.elaborationType.toLowerCase() === "change_doc_structure")
          operationName = "changedocumentstructure";
        else operationName = elaboration.items[0]?.elaborationType.toLowerCase() as typeof operationName;

        console.log(operationName);

        let operationStatus: "success" | "failed" | "warning" | "aborted" = "success";
        let notificationType: NotificationToastToAdd["type"] = "success";
        if (elaboration.items.some((item) => item.status === "ABORTED")) {
          operationStatus = "aborted";
          notificationType = "warning";
        } else if (elaboration.items.some((item) => item.status !== "COMPLETED")) {
          //rejected
          operationStatus = "failed";
          notificationType = "error";
        } else if (errorsAndWarnings.errors.length > 0) {
          operationStatus = "failed";
          notificationType = "error";
        } else if (errorsAndWarnings.warnings.length > 0) {
          operationStatus = "warning";
          notificationType = "warning";
        }

        let msg = formatMessageWithParams(`cof.elaboration.${operationName}.message.${operationStatus}`, {
          startDate: elaboration.items[0].startDate,
          endDate: elaboration.items[0].endDate,
        });
        let title = formatMessage(`cof.elaboration.${operationName}.title.${operationStatus}`);

        if (
          elaboration.items[0].elaborationType === "SHEET_AND_AREA" ||
          elaboration.items[0].elaborationType === "DOWNLOAD_COPY"
        ) {
          onConfirmElaboration(elaboration);
        } else
          NotificationManager.addNotification({
            key: elaboration.correlationId,
            title: title,
            message: msg,
            type: notificationType,
            closable: true,
            onClose: () => {
              onConfirmElaboration(elaboration);
            },
            removeOnClose: notificationType == "success",
            details: errorsAndWarnings.errors.concat(errorsAndWarnings.warnings),
            isTemporary:
              notificationType == "success" &&
              operationName != "updateobject" &&
              operationName != "duplicate" &&
              operationName != "import",
          });
      }
    }
  } finally {
    isProcessing = false;
    instances--;
  }
};

export function abortAbortableElaborationStatusItems(items: ElaborationStatusItem[]) {
  const correlationIds = new Set<string>();
  items.forEach((item) => {
    if (checkAbortable(item)) correlationIds.add(item.correlationId);
  });

  abortRefreshAsync({
    correlationIds: Array.from(correlationIds),
  });
}
